import React from 'react';
import {ForecastTooltipFormulaRenderer} from '../../../../../../components/ForecastTooltipFormulaRenderer';
import {ValueCell} from '../../../../../../components/initial-plan/ValueCell';
import ForecastTooltip from '../../../../../shared/components/tooltips/ForecastTooltip';
import {remapId} from '../../../../../shared/util/FinancialInternationalisationUtil';
import {Data, HorizontalBar, Title, ValueContainer} from '../ProjectScopingTotals_styled';
import {ProjectScopingTotalsTooltips} from '../DeprecatedProjectScopingTotalsTooltips';
import {AvailabilityTile} from './AvailabilityTile';
import {Tile, TileRow} from './Tile_styled';
import {useIntl} from 'react-intl';
import {useProjectTotals} from '../ProjectScopingTotalsContext';

interface DatesAndRevenueTileProps {
	showRevenue: boolean;
	isFixedPriceV2: boolean;
	hasFinancialServiceInScoping: boolean;
	showAvailabilityTile: boolean;
}

const DatesRow = () => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: 'project_overview.project_dates'})}</Title>
			<Data small>{`${totalsData.startDateString} - ${totalsData.endDateString}`}</Data>
		</TileRow>
	);
};

const ProgressRow = () => {
	const intl = useIntl();
	const {totalsData, projectData} = useProjectTotals();
	return (
		<TileRow>
			<Title>
				{intl.formatMessage({
					id: projectData?.manualProgressOnProjectEnabled
						? 'common.progress'
						: 'project_scoping.estimated_project_progress',
				})}
			</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.PERCENTAGE} value={totalsData.progress} />
			</Data>
		</TileRow>
	);
};

const FixedPriceRow = ({isFixedPriceV2}: Pick<DatesAndRevenueTileProps, 'isFixedPriceV2'>) => {
	const intl = useIntl();
	const {totalsData, projectData, settings} = useProjectTotals();
	if (isFixedPriceV2) {
		return (
			<TileRow>
				<Title singleLine>{intl.formatMessage({id: remapId('project_budget.fixed_price')})}</Title>
				<Data small>
					<ValueCell
						noJustify
						type={ValueCell.VALUE_TYPE.PRICE}
						value={projectData?.budget || 0}
						currencySymbol={settings.currencySymbol}
					/>
				</Data>
			</TileRow>
		);
	} else if (!settings.isPointsProject && settings.financialSourceSettings?.plannedRevenue === 'TASK') {
		return (
			<TileRow>
				<Title singleLine>
					{intl.formatMessage({
						id: remapId('project_scoping.total_planned_billable_time'),
					})}
				</Title>
				<Data small>
					<ValueCell
						noJustify
						type={ValueCell.VALUE_TYPE.PRICE}
						value={totalsData.plannedRevenue}
						currencySymbol={settings.currencySymbol}
					/>
				</Data>
			</TileRow>
		);
	}

	return null;
};

const ActualPriceRow = ({hasFinancialServiceInScoping}: Pick<DatesAndRevenueTileProps, 'hasFinancialServiceInScoping'>) => {
	const intl = useIntl();
	const {totalsData, settings} = useProjectTotals();
	if (hasFinancialServiceInScoping) {
		if (
			settings.financialSourceSettings?.actualRevenue === 'TASK' &&
			settings.financialSourceSettings?.forecastRevenue === 'TASK'
		) {
			return (
				<TileRow>
					<Title singleLine>
						{intl.formatMessage({
							id: 'project_budget.total_value_of_service_at_completion',
						})}
					</Title>
					<Data small>
						<ForecastTooltip
							placement={'bottom'}
							maxWidth={'350px'}
							content={
								<ForecastTooltipFormulaRenderer
									items={[
										{
											title: intl.formatMessage({
												id: 'project_budget.total_value_of_service_at_completion',
											}),
											details: [
												intl.formatMessage({id: 'common.time'}),
												'x',
												intl.formatMessage({id: 'common.rate'}),
												'+',
												intl.formatMessage({id: 'common.expenses'}),
											],
											footer: intl.formatMessage({
												id: 'project_budget.total_value_of_service_at_completion_tooltip',
											}),
										},
									]}
									translatedMessage={true}
								/>
							}
						>
							<ValueContainer>
								<ValueCell
									noJustify
									type={ValueCell.VALUE_TYPE.PRICE}
									value={totalsData.allTotalTimeAndExpensesAtCompletion}
									currencySymbol={settings.currencySymbol}
								/>
							</ValueContainer>
						</ForecastTooltip>
					</Data>
				</TileRow>
			);
		}

		return null;
	}

	return (
		<TileRow>
			<Title singleLine>{intl.formatMessage({id: remapId('project_budget.actual_revenue_no_expenses')})}</Title>
			<Data small>
				<ForecastTooltip placement={'bottom'} content={ProjectScopingTotalsTooltips()}>
					<ValueContainer>
						<ValueCell
							noJustify
							type={ValueCell.VALUE_TYPE.PRICE}
							value={totalsData.actualRevenue}
							currencySymbol={settings.currencySymbol}
						/>
					</ValueContainer>
				</ForecastTooltip>
			</Data>
		</TileRow>
	);
};

export const DatesAndRevenueTile = ({
	showRevenue,
	isFixedPriceV2,
	hasFinancialServiceInScoping,
	showAvailabilityTile,
}: DatesAndRevenueTileProps): JSX.Element => {
	return (
		<Tile>
			<DatesRow />
			<ProgressRow />
			{showRevenue ? (
				<>
					<HorizontalBar />
					<FixedPriceRow isFixedPriceV2={isFixedPriceV2} />
					<ActualPriceRow hasFinancialServiceInScoping={hasFinancialServiceInScoping} />
				</>
			) : showAvailabilityTile ? (
				<>
					<HorizontalBar />
					<AvailabilityTile />
				</>
			) : null}
		</Tile>
	);
};
